<script setup lang="ts">
import { useSwipeToDismiss } from '@/composables/useSwipeToDismiss';
import { ErrorIcon, InfoIcon, SuccessIcon, WarningIcon } from '@/icons';
import { useToastStore } from '@/stores/toast';
import { useI18n } from 'vue-i18n';

const toast = useToastStore();
const { t: translate } = useI18n();

// Swipe a toast upwards to get rid of it (touch only).
const { onTouchStart, onTouchMove, onTouchEnd, styleFor } = useSwipeToDismiss({
    onDismiss: (id) => toast.remove(id),
});

function classes(type: string) {
    const base =
        'flex items-center justify-between gap-3 w-full sm:max-w-[340px] rounded-md border-b-4 bg-white p-3 shadow-theme-sm dark:bg-[#1E2634] shadow-theme-sm self-end';
    const tone: Record<string, string> = {
        success: 'border-success-500',
        error: 'border-error-500',
        info: 'border-blue-light-500',
        warning: 'border-warning-500',
    };
    return `${base} ${tone[type] || 'border-gray-200'}`;
}
</script>

<template>
    <div class="pointer-events-none fixed inset-0 z-[9999999]">
        <div
            class="absolute top-5 right-5 left-5 flex max-w-sm flex-col gap-3 md:left-auto md:w-full"
            role="region"
            :aria-label="translate('common.notifications')"
        >
            <TransitionGroup name="toast" tag="div" class="flex flex-col justify-end">
                <div
                    v-for="t in toast.items"
                    :key="t.id"
                    :class="classes(t.type)"
                    class="pointer-events-auto touch-none"
                    :style="styleFor(t.id)"
                    :role="t.type === 'error' || t.type === 'warning' ? 'alert' : 'status'"
                    :aria-live="t.type === 'error' || t.type === 'warning' ? 'assertive' : 'polite'"
                    aria-atomic="true"
                    data-cy="toast"
                    @touchstart.passive="onTouchStart(t.id, $event)"
                    @touchmove.passive="onTouchMove($event)"
                    @touchend="onTouchEnd"
                    @touchcancel="onTouchEnd"
                >
                    <div class="flex items-center gap-4">
                        <div class="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg">
                            <SuccessIcon
                                class="fill-current text-success-600 dark:text-success-500"
                                v-if="t.type === 'success'"
                                >✅</SuccessIcon
                            >
                            <ErrorIcon
                                class="fill-current text-error-600 dark:text-error-500"
                                v-else-if="t.type === 'error'"
                                >⛔</ErrorIcon
                            >
                            <WarningIcon
                                class="fill-current text-warning-600 dark:text-warning-500"
                                v-else-if="t.type === 'warning'"
                                >⚠️</WarningIcon
                            >
                            <InfoIcon class="fill-current text-blue-light-500 dark:text-blue-light-500" v-else
                                >ℹ️</InfoIcon
                            >
                        </div>
                        <p class="text-sm text-gray-800 sm:text-base dark:text-white/90">{{ t.message }}</p>
                    </div>
                    <button
                        type="button"
                        class="flex items-center justify-center text-gray-400 hover:text-gray-800 dark:hover:text-white/90 pointer-coarse:min-h-11 pointer-coarse:min-w-11"
                        @click="toast.remove(t.id)"
                        data-cy="close-toast-btn"
                        :aria-label="translate('common.close')"
                    >
                        ✕
                    </button>
                </div>
            </TransitionGroup>
        </div>
    </div>
</template>

<style scoped>
.toast-enter-active,
.toast-leave-active {
    transition: all 0.18s ease;
}
.toast-enter-from {
    opacity: 0;
    transform: translateY(-6px) scale(0.98);
}
.toast-leave-to {
    opacity: 0;
    transform: translateY(-6px) scale(0.98);
}
</style>
